import { useLayoutEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import RestrictedComponent from "common/components/RestrictedComponent";
import {
  CreateOrEditTemplate,
  DesktopTemplate,
  MobileTemplate,
  TemplatePreview,
} from "common/components/Templates";
import { Button } from "common/components/ui/Button";
import { TEMPLATE_TYPES } from "common/constants";
import useTemplates from "common/datahooks/useTemplates";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { ContentHeader, ContentScroll } from "common/layouts/WorkspaceLayout";
import type { Template } from "common/types";

import DesktopEditTemplate from "./DesktopEditTemplate";
import DiscardEditsDialog from "./DiscardEditsDialog";
import MobilePreview from "./MobilePreview";
import TemplateActions from "./TemplateActions";

export default function SelectedTemplate() {
  const isDesktop = useTwBreakpoint("lg");
  const { templateId } = useParams();
  const { allTemplates } = useTemplates();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { accounts } = useSelectedWorkspaceContext();

  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template>();

  const selectedTemplate = allTemplates?.find(({ id }) => id === templateId);

  //layout effect prevents no template placeholder message flashing
  useLayoutEffect(() => {
    if (selectedTemplate) {
      setPreviewTemplate(selectedTemplate);
    }
  }, [selectedTemplate]);

  if (selectedTemplate) {
    const { message, type, id, name } = selectedTemplate;

    return (
      <>
        <CreateOrEditTemplate
          key={selectedTemplate.id}
          initialData={selectedTemplate}
          onSuccess={() => {
            setPreviewTemplate(selectedTemplate);
          }}
        >
          {(onSubmit, isLoading, form) => {
            const updatedTemplate = form.watch();
            const isTemplateUnchanged =
              updatedTemplate.message === message &&
              ("subject" in updatedTemplate && "subject" in selectedTemplate
                ? updatedTemplate.subject === selectedTemplate.subject
                : true) &&
              Object.keys(updatedTemplate.ai_descriptors).every(
                (key) =>
                  updatedTemplate.ai_descriptors[key] ===
                  selectedTemplate.ai_descriptors[key],
              );

            const previewComponent = (
              <TemplatePreview
                previewTemplate={previewTemplate}
                updatedTemplate={updatedTemplate}
                onUpdatePreview={() => setPreviewTemplate(updatedTemplate)}
                senderProfiles={accounts}
              />
            );

            return isDesktop ? (
              <>
                <ContentHeader>
                  <h2 className="text-md-bold lg:text-lg-bold">{`${TEMPLATE_TYPES[type].shortLabel} template`}</h2>

                  <RestrictedComponent
                    visibleForPermissions={["templates_full"]}
                  >
                    <TemplateActions templateId={id} templateName={name} />
                  </RestrictedComponent>
                </ContentHeader>

                <ContentScroll className="p-4 gap-y-4">
                  <DesktopEditTemplate template={selectedTemplate} />
                  <RestrictedComponent
                    enabledForPermissions={["templates_full"]}
                  >
                    {(isDisabled) => (
                      <DesktopTemplate
                        previewComponent={previewComponent}
                        form={form}
                        isDisabled={isDisabled}
                      />
                    )}
                  </RestrictedComponent>
                </ContentScroll>

                {/* Footer */}
                <div className="p-2.5 border-t border-black-200 flex justify-between">
                  <Button
                    variant="secondary-black"
                    onClick={
                      isTemplateUnchanged
                        ? () => navigate({ pathname: "..", search })
                        : () => setIsDiscardOpen(true)
                    }
                    disabled={isLoading}
                  >
                    Close
                  </Button>
                  <RestrictedComponent
                    visibleForPermissions={["templates_full"]}
                  >
                    <Button
                      onClick={onSubmit}
                      isLoading={isLoading}
                      disabled={isTemplateUnchanged}
                    >
                      Save
                    </Button>
                  </RestrictedComponent>
                </div>
              </>
            ) : (
              <MobilePreview template={selectedTemplate}>
                {(goBack) => {
                  return (
                    <MobileTemplate
                      onClose={
                        isTemplateUnchanged
                          ? goBack
                          : () => setIsDiscardOpen(true)
                      }
                      saveButton={
                        <Button
                          onClick={() => onSubmit().then(goBack)}
                          isLoading={isLoading}
                          disabled={isTemplateUnchanged}
                          size="lg"
                        >
                          Save
                        </Button>
                      }
                      previewComponent={previewComponent}
                      updatePreview={() => setPreviewTemplate(updatedTemplate)}
                      form={form}
                    />
                  );
                }}
              </MobilePreview>
            );
          }}
        </CreateOrEditTemplate>

        <DiscardEditsDialog
          isOpen={isDiscardOpen}
          setIsOpen={setIsDiscardOpen}
        />
      </>
    );
  }
}
